import React, { useState, useEffect, useRef } from 'react';
import VideoPlayer from '../../components/VideoPlayer';
import {
  LuMonitorPlay,
  LuRadio,
  LuBookOpen,
  LuSchool,
  LuUsers,
  LuExternalLink,
  LuPlay
} from 'react-icons/lu';
import { PiChurchLight } from 'react-icons/pi';
import { FaYoutube } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import { extractString } from '../../shared/fieldUtils';
import livestreamHero from '../../assets/livestream_hero.jpg';
import churchThumb from '../../assets/cathedral_aerial_hero.jpg';



function getYTThumbnail(url) {
  if (!url) return '';
  const ytMatch = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|live\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  if (ytMatch && ytMatch[1]) {
    return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
  }
  return '';
}

export default function LiveStreaming({ activeSection = 'live', onSelectSection = () => { } }) {
  const [streams, setStreams] = useState([]);
  const [activeStream, setActiveStream] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const playerRef = useRef(null);

  const handleStartPlay = (stream = null) => {
    if (stream) setActiveStream(stream);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (isPlaying && playerRef.current) {
      const timer = setTimeout(() => {
        if (playerRef.current) {
          const yOffset = -110;
          const y = playerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, activeStream]);

  useEffect(() => {
    let isMounted = true;

    async function fetchFirebaseLiveStreams() {
      try {
        setLoading(true);
        const collectionsToTry = [
          'youtube',
          'youtubes',
          'youtube-video',
          'youtube_video',
          'livestream',
          'live-stream',
          'live_stream'
        ];

        const fetchedItems = [];
        const seenLinks = new Set();

        for (const colName of collectionsToTry) {
          const snap = await getDocs(collection(db, colName)).catch(() => null);
          if (snap && !snap.empty) {
            snap.docs.forEach((doc) => {
              const d = doc.data();
              const rawLink = extractString(
                d.link ||
                d.url ||
                d.video_url ||
                d.videoLink ||
                d.youtube_link
              );

              const isYouTube = rawLink && (
                rawLink.includes('youtube.com') ||
                rawLink.includes('youtu.be') ||
                Boolean(getYTThumbnail(rawLink))
              );

              if (rawLink && isYouTube && !seenLinks.has(rawLink)) {
                seenLinks.add(rawLink);

                let rawTitle = extractString(d.title || d.name || d.caption || d.heading, "");
                const rawDate = extractString(d.createdAt || d.date || d.timestamp || d.created_at || d.schedule, "");
                const rawDesc = extractString(d.desc || d.description || d.subtitle || d.details, "");

                fetchedItems.push({
                  id: doc.id,
                  title: rawTitle,
                  link: rawLink,
                  createdAt: rawDate,
                  desc: rawDesc,
                  isLive: d.isLive !== undefined ? Boolean(d.isLive) : false,
                  sourceCollection: colName
                });
              }
            });
          }
        }

        if (isMounted) {
          // Sort newest first
          fetchedItems.sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime() || 0;
            const dateB = new Date(b.createdAt).getTime() || 0;
            return dateB - dateA;
          });

          setStreams(fetchedItems);
          if (fetchedItems.length > 0) {
            setActiveStream(fetchedItems[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching live streams from Firebase:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchFirebaseLiveStreams();
    return () => { isMounted = false; };
  }, []);

  const featured = activeStream || streams[0] || null;



  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={livestreamHero}
            alt="Live Streaming"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              LIVE STREAMING
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              Join our Holy Eucharistic worship services, bishop's messages, conventions, and choir concerts live from anywhere across the globe.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Live Streaming</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuMonitorPlay className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">HD Video</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Live Broadcast</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuRadio className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">Sunday Services</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">6:00 AM &amp; 8:30 AM</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 space-y-10">

        {/* Main Live Player Window */}
        {featured && (
          <div ref={playerRef} className="bg-[#1C1622] rounded-[28px] overflow-hidden shadow-2xl border border-white/10 relative scroll-mt-32">
            <div className="relative aspect-video w-full flex items-center justify-center overflow-hidden bg-black">
              {isPlaying && featured?.link ? (
                <VideoPlayer
                  item={featured}
                  url={featured.link}
                  title={featured.title || "Live Stream"}
                  autoplay={true}
                />
              ) : (
                <div className="relative w-full h-full flex items-center justify-center">
                  {getYTThumbnail(featured.link) ? (
                    <img
                      src={getYTThumbnail(featured.link)}
                      alt={featured.title || "Live Stream"}
                      className="absolute inset-0 w-full h-full object-cover filter brightness-[0.45]"
                    />
                  ) : (
                    <img
                      src={churchThumb}
                      alt="Diocese of Malabar"
                      className="absolute inset-0 w-full h-full object-cover filter brightness-[0.35]"
                    />
                  )}

                  <div className="relative z-10 text-center text-white p-6 max-w-2xl">
                    <div className="inline-flex items-center gap-2 bg-red-600 px-3.5 py-1 rounded-full text-xs font-extrabold tracking-widest uppercase mb-4 shadow-[0_0_15px_rgba(220,38,38,0.5)]">
                      <span className="w-2 h-2 rounded-full bg-white animate-ping"></span>
                      Live Video Broadcast
                    </div>
                    {featured.title && featured.title.trim() !== '' && (
                      <h2 className="font-serif font-bold text-2xl sm:text-3xl md:text-4xl mb-3 text-[#f0dfba] drop-shadow-md">
                        {featured.title}
                      </h2>
                    )}
                    {featured.desc && featured.desc.trim() !== '' && (
                      <p className="text-sm text-white/80 mb-6 max-w-lg mx-auto line-clamp-2">
                        {featured.desc}
                      </p>
                    )}
                    <button
                      onClick={() => handleStartPlay()}
                      className="bg-[#842343] hover:bg-[#6c1b35] text-white font-sans font-semibold text-sm px-8 py-3.5 rounded-full shadow-[0_6px_20px_rgba(132,35,67,0.4)] transition-all duration-300 hover:scale-105 active:scale-95 inline-flex items-center gap-2.5 cursor-pointer border border-white/20 mt-2"
                    >
                      <LuPlay className="w-4 h-4 fill-white" />
                      <span>Watch Live Stream</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dynamic Broadcasts Grid from Firebase */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-serif font-bold text-2xl sm:text-3xl text-[#842343]">
              Diocesan Broadcasts &amp; Videos
            </h2>
            {loading && (
              <span className="text-xs text-[#8A7E79] font-medium animate-pulse">
                Fetching latest streams...
              </span>
            )}
          </div>

          {streams.length === 0 && !loading ? (
            <div className="bg-white rounded-[22px] p-8 text-center border border-gray-100 text-[#8A7E79] font-medium">
              No live stream broadcasts available at this moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {streams.map((b) => {
                const isCurrent = activeStream && activeStream.id === b.id;
                const ytThumb = getYTThumbnail(b.link);

                return (
                  <div
                    key={b.id}
                    onClick={() => handleStartPlay(b)}
                    className={`bg-white rounded-[22px] overflow-hidden border transition-all duration-300 cursor-pointer flex flex-col justify-between group hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] ${isCurrent ? 'border-[#842343] ring-2 ring-[#842343]/20 shadow-md' : 'border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]'
                      }`}
                  >
                    {/* Thumbnail Image Header - YouTube auto-thumbnail or church fallback */}
                    <div className="relative aspect-video w-full overflow-hidden bg-black">
                      <img
                        src={ytThumb || churchThumb}
                        alt={b.title || "Video Broadcast"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-[#842343]/90 text-white flex items-center justify-center pl-0.5 shadow-lg group-hover:scale-110 transition-transform">
                          <LuPlay className="w-4 h-4 fill-white" />
                        </div>
                      </div>
                      {b.isLive && (
                        <div className="absolute top-3 left-3 bg-red-600 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold tracking-widest text-white flex items-center gap-1.5 shadow-[0_0_10px_rgba(220,38,38,0.5)]">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                          LIVE
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#842343] bg-[#fdf0f3] px-2.5 py-1 rounded-full flex items-center gap-1.5">
                            <FaYoutube className="w-3.5 h-3.5 text-red-600" />
                            YouTube Video
                          </span>
                          {b.createdAt && (
                            <span className="text-[11px] text-[#8A7E79]">
                              {isNaN(new Date(b.createdAt).getTime())
                                ? b.createdAt
                                : new Date(b.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </div>

                        {b.title && b.title.trim() !== '' && (
                          <h3 className="font-serif font-bold text-[16px] text-[#1a1715] mb-2 leading-snug group-hover:text-[#842343] transition-colors line-clamp-2">
                            {b.title}
                          </h3>
                        )}

                        {b.desc && b.desc.trim() !== '' && (
                          <p className="text-xs text-[#5A4F4A] leading-relaxed mb-4 line-clamp-2">
                            {b.desc}
                          </p>
                        )}
                      </div>

                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-medium">
                        <span className="text-[#842343] flex items-center gap-1.5 font-semibold">
                          <LuPlay className="w-3.5 h-3.5 fill-[#842343]" />
                          <span>Watch Now</span>
                        </span>
                        {b.link && (
                          <a
                            href={b.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[#8A7E79] hover:text-[#842343] flex items-center gap-1 transition-colors"
                          >
                            <span>Open</span>
                            <LuExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* ── Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">26+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Dioceses</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">5</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuSchool className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">120+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Institutions</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
